import React, { useState, useRef, useEffect } from 'react';
import './BulkImport.css';

const STATUS = {
PENDING: 'pending',
PROCESSING: 'processing',
SUCCESS: 'success',
ERROR: 'error',
SKIPPED: 'skipped',
};

const STATUS_ICON = {
pending: '⏳',
processing: '',
success: '',
error: '',
skipped: '⏭',
};

export default function BulkImport() {
const [inputText, setInputText] = useState('');
const [queue, setQueue] = useState([]);
const [running, setRunning] = useState(false);
const [done, setDone] = useState(false);
const logEndRef = useRef(null);

useEffect(() => {
if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior: 'smooth' });
}, [queue]);

// Parse textarea into import items.
// Supports three formats (can be mixed freely):
// 1. Plain URL line -> url_only
// 2. URL + non-URL text lines below -> url_with_text (Facebook/YouTube + recipe text)
// 3. Non-URL text block -> raw_text (pasted recipe with no URL)
const parseItems = (text) => {
const lines = text.split('\n').map(l => l.replace(/^\[recipe\]\s*/i, '').trim());
const items = [];
let i = 0;

while (i < lines.length) {
const line = lines[i];
if (!line) { i++; continue; }

const isUrl = /^https?:\/\//i.test(line);

if (isUrl) {
const textLines = [];
let j = i + 1;
while (j < lines.length && lines[j] && !/^https?:\/\//i.test(lines[j])) {
textLines.push(lines[j]);
j++;
}
if (textLines.length > 0) {
items.push({ url: line, pastedText: textLines.join('\n'), mode: 'url_with_text', display: line });
} else {
items.push({ url: line, pastedText: '', mode: 'url_only', display: line });
}
i = j;
} else {
const textLines = [line];
let j = i + 1;
while (j < lines.length && lines[j] && !/^https?:\/\//i.test(lines[j])) {
textLines.push(lines[j]);
j++;
}
items.push({ url: null, pastedText: textLines.join('\n'), mode: 'raw_text', display: textLines[0] });
i = j;
}
}
return items;
};

const handleStart = () => {
const items = parseItems(inputText);
if (items.length === 0) { alert('Please enter at least one recipe URL or text.'); return; }
if (items.length > 25) {
if (!window.confirm(`You entered ${items.length} recipes. This may take a while. Continue?`)) return;
}
const queueItems = items.map((item, idx) => ({
id: idx, ...item, status: STATUS.PENDING, message: '', recipeUrl: null,
}));
setQueue(queueItems);
setRunning(true);
setDone(false);
processQueue(queueItems);
};

const processQueue = async (items) => {
const updatedItems = [...items];
updatedItems[0] = { ...updatedItems[0], status: STATUS.PROCESSING };
setQueue([...updatedItems]);

try {
const token = localStorage.getItem('rs_token');

const messages = items.map(item => {
if (item.mode === 'url_with_text') return '[recipe] ' + item.url + '\n' + item.pastedText;
if (item.mode === 'raw_text') return '[recipe] ' + item.pastedText;
return '[recipe] ' + item.url;
});

const fetchRes = await fetch('/api/recipes/bulk-import', {
method: 'POST',
headers: { 'Authorization': 'Bearer ' + token, 'Content-Type': 'application/json' },
body: JSON.stringify({ urls: items.map(i => i.url || i.pastedText), messages }),
});

const data = await fetchRes.json();

if (data.success) {
const triggered = data.triggered || [];
for (let i = 0; i < updatedItems.length; i++) {
const t = triggered[i];
const ok = t && t.triggered !== false;
let msg = 'Failed to queue';
if (ok) {
if (updatedItems[i].mode === 'url_with_text') msg = 'Queued with recipe text!';
else if (updatedItems[i].mode === 'raw_text') msg = 'Queued as pasted text!';
else msg = 'Queued! Watch Telegram for updates.';
}
updatedItems[i] = { ...updatedItems[i], status: ok ? STATUS.SUCCESS : STATUS.ERROR, message: msg };
}
setQueue([...updatedItems]);
} else {
throw new Error(data.error || 'Bulk import failed');
}
} catch (err) {
for (let i = 0; i < updatedItems.length; i++) {
updatedItems[i] = { ...updatedItems[i], status: STATUS.ERROR, message: err.message || 'Failed' };
}
setQueue([...updatedItems]);
}

setRunning(false);
setDone(true);
};

const handleReset = () => { setQueue([]); setInputText(''); setRunning(false); setDone(false); };

const parsedItems = parseItems(inputText);
const urlCount = parsedItems.filter(i => i.mode === 'url_only').length;
const mixedCount = parsedItems.filter(i => i.mode === 'url_with_text').length;
const textCount = parsedItems.filter(i => i.mode === 'raw_text').length;

const counts = {
total: queue.length,
success: queue.filter(i => i.status === STATUS.SUCCESS).length,
error: queue.filter(i => i.status === STATUS.ERROR).length,
};
const progress = counts.total > 0 ? Math.round(((counts.success + counts.error) / counts.total) * 100) : 0;

return (
<div className="bulk-import">
<div className="container">
<div className="bulk-import__header">
<h1> Bulk Recipe Import</h1>
<p>Paste up to 25 recipes below. You can mix all three formats freely. Recipes are processed one at a time with a 90-second gap between each — <strong>allow ~2 minutes per recipe</strong>. You'll get a Telegram message when each one completes.</p>
<div className="bulk-import__formats">
<div className="bulk-import__format">
<strong> URL only</strong>
<code>https://www.budgetbytes.com/gumbo/</code>
</div>
<div className="bulk-import__format">
<strong> Video URL + recipe text</strong>
<code>https://facebook.com/reel/123</code>
<code>Chicken Tacos</code>
<code>2 cups chicken, 1 tsp cumin...</code>
</div>
<div className="bulk-import__format">
<strong> Pasted text only</strong>
<code>Crockpot Chicken Spaghetti</code>
<code>3 chicken breasts, 20oz cream of chicken...</code>
</div>
</div>
</div>

{!running && !done && (
<div className="bulk-import__input-section">
<label className="bulk-import__label">
Recipes <span className="bulk-import__hint">(mix URLs and pasted text freely)</span>
</label>
<textarea
className="bulk-import__textarea"
value={inputText}
onChange={e => setInputText(e.target.value)}
placeholder={'https://www.allrecipes.com/recipe/123/\nhttps://facebook.com/reel/456\nChicken Tacos\n2 lbs chicken\n1 tsp cumin...\nhttps://www.budgetbytes.com/gumbo/'}
rows={12}
/>
<div className="bulk-import__input-footer">
<span className="bulk-import__count">
{parsedItems.length > 0 ? (
<>
{parsedItems.length} recipe{parsedItems.length !== 1 ? 's' : ''} detected
{urlCount > 0 && <span> · {urlCount} URL{urlCount !== 1 ? 's' : ''}</span>}
{mixedCount > 0 && <span> · {mixedCount} video+text</span>}
{textCount > 0 && <span> · {textCount} text-only</span>}
</>
) : 'No recipes detected yet'}
</span>
<button
className="btn btn-primary bulk-import__start-btn"
onClick={handleStart}
disabled={parsedItems.length === 0}
>
Start Import
</button>
</div>
</div>
)}

{(running || done) && (
<div className="bulk-import__progress-section">
<div className="bulk-import__summary">
<div className="bulk-import__summary-stat">
<span className="bulk-import__summary-num">{counts.total}</span>
<span className="bulk-import__summary-label">Total</span>
</div>
<div className="bulk-import__summary-stat bulk-import__summary-stat--success">
<span className="bulk-import__summary-num">{counts.success}</span>
<span className="bulk-import__summary-label">Queued</span>
</div>
<div className="bulk-import__summary-stat bulk-import__summary-stat--error">
<span className="bulk-import__summary-num">{counts.error}</span>
<span className="bulk-import__summary-label">Failed</span>
</div>
</div>
<div className="bulk-import__progress-bar">
<div className="bulk-import__progress-fill" style={{ width: `${progress}%` }} />
</div>
<div className="bulk-import__progress-label">
{done
? `Done! ${counts.success} recipe${counts.success !== 1 ? 's' : ''} queued via Telegram. Watch for messages!`
: 'Queuing recipes...'}
</div>
<div className="bulk-import__controls">
{done && <button className="btn btn-outline" onClick={handleReset}>↩ Import More</button>}
</div>
<div className="bulk-import__log">
{queue.map((item) => (
<div key={item.id} className={`bulk-import__log-item bulk-import__log-item--${item.status}`}>
<span className="bulk-import__log-icon">{STATUS_ICON[item.status]}</span>
<div className="bulk-import__log-content">
<span className="bulk-import__log-url">
{item.display && item.display.length > 65 ? item.display.substring(0, 65) + '...' : item.display}
</span>
{item.mode !== 'url_only' && (
<span className="bulk-import__log-mode">
{item.mode === 'url_with_text' ? ' Video + text' : ' Text only'}
</span>
)}
{item.message && <span className="bulk-import__log-msg">{item.message}</span>}
</div>
{item.status === STATUS.PROCESSING && <span className="bulk-import__log-spinner" />}
</div>
))}
<div ref={logEndRef} />
</div>
</div>
)}
</div>
</div>
);
}
